// `HeroFigure` (docs/145): a hero drawn from the hero kit (game/heroKit.ts) in the box an Oryx frame took: a
// 24 px board cell at `scale`, with the kit canvas's overhang (hats, polearms, capes) spilling out of it
// as the Oryx art never did. `breathe` plays KH3's two frames on its own; `frame` sets one (a caller keeping
// its own beat, as the village does). Until the look is baked it holds its box empty.
import { useEffect, useMemo, useState, type CSSProperties, type ReactElement } from 'react';
import { bakeHero, bakedHero, classLook, KIT_CELL, KIT_H, KIT_W, type HeroFrames, type HeroLook } from '../../game/heroKit';
import { useHeroLook } from '../../core/heroes/looks';

export interface HeroFigureProps {
  /** The look to draw; else the hero's own (by id) from the store. */
  look?: HeroLook;
  heroId?: bigint | number;
  /** For a hero the cache hasn't got: its species and class, drawn in the class's plain kit (KH5). */
  species?: number;
  classId?: string;
  scale?: number;
  /** Breathe on its own (KH3). */
  breathe?: boolean;
  /** Show one frame (0 standing, 1 breathing out); wins over `breathe`. */
  frame?: 0 | 1;
  mirror?: boolean;
  title?: string;
  className?: string;
  style?: CSSProperties;
}

function useFrames(look: HeroLook): HeroFrames | undefined {
  const [, bump] = useState(0);
  const now = bakedHero(look);
  useEffect(() => {
    if (now) return;
    let live = true;
    void bakeHero(look).then(() => { if (live) bump((n) => n + 1); });
    return () => { live = false; };
  }, [look, now]);
  return now;
}

/** KH3: how long one breath takes (both frames), and each hero's place in it, so a band doesn't breathe as one. */
export const BREATH_MS = 1400;
const breathOffset = (seed: bigint | number) => Number(BigInt.asUintN(32, BigInt(seed) * 2654435761n) % BigInt(BREATH_MS));

export function HeroFigure({ look, heroId, species, classId, scale = 2, breathe, frame, mirror, title, className = '', style }: HeroFigureProps) {
  const stored = useHeroLook(look ? undefined : heroId);
  const fallback = useMemo(() => classLook(classId, species ?? 0), [classId, species]);
  const l = look ?? stored ?? fallback;
  const frames = useFrames(l);
  const box = KIT_CELL.size * scale;
  const img = (url: string, extra = ''): ReactElement => (
    <img src={url} alt="" draggable={false} className={`fk-herofig__frame ${extra}`}
      style={{ left: -KIT_CELL.x * scale, top: -KIT_CELL.y * scale, width: KIT_W * scale, height: KIT_H * scale }} />
  );
  const animated = frame === undefined && breathe;
  return (
    <span className={`fk-sprite fk-herofig ${animated ? 'fk-herofig--breathe' : ''} ${className}`} role="img" aria-label={title ?? 'hero'} title={title}
      data-look={frames?.key} style={{ width: box, height: box, ...(mirror ? { transform: 'scaleX(-1)' } : {}), ...(animated ? { ['--fk-breath-delay' as string]: `${-breathOffset(heroId ?? species ?? 0)}ms` } : {}), ...style }}>
      {frames && (frame === 1 ? img(frames.urlB) : animated ? <>{img(frames.urlA, 'fk-herofig__a')}{img(frames.urlB, 'fk-herofig__b')}</> : img(frames.urlA))}
    </span>
  );
}
